import React from "react";
import "./TermsAndConditions.scss";

const TermsAndConditions = () => {
  return (
    <div className="terms-page">
      <div className="terms-container">

        {/* header with accent line and refined typography */}
        <div className="terms-header">
          <span className="header-tag">Legal</span>
          <h1>Terms & Conditions</h1>
          <h2>World Adventure Tours</h2>
          <p className="subtitle">
            Adventure Travel · Trekking · Expeditions in Pakistan
          </p>
          <div className="effective-date">
            <strong>Effective:</strong> March 2026
          </div>
        </div>

        {/* intro – clear and direct */}
        <div className="terms-intro">
          <p>
            By booking or paying a deposit for any tour, trek, or expedition with <strong>World Adventure Tours</strong>, you acknowledge that you have read, understood, and agree to be legally bound by these Terms and Conditions. We are committed to responsible and professional adventure travel in Pakistan.
          </p>
        </div>

        {/* 2‑column layout for compact sections, only on medium+ screens */}
        <div className="sections-grid">
          {/* SECTION 1 */}
          <section className="grid-item">
            <h3>1. Booking & Confirmation</h3>
            <ul>
              <li>Deposit (25–30%) and written email confirmation required to secure booking.</li>
              <li>Final balance due 60 days before departure.</li>
              <li>All prices in USD — international flights, visas, insurance, tips not included.</li>
              <li>Prices may change before confirmation due to currency or permit adjustments.</li>
            </ul>
          </section>

          {/* SECTION 2 – flexible cancellation (highlighted as card) */}
          <section className="grid-item highlight-card">
            <h3>2. Flexible Cancellation & Credit</h3>
            <div className="cancellation-options">
              <div className="option">
                <span className="option-title">Option A – Cash refund</span>
                <ul>
                  <li>&gt;90 days: full refund minus 5% admin</li>
                  <li>61–90 days: 75% refund</li>
                  <li>31–60 days: 50% refund</li>
                  <li>≤30 days: no refund</li>
                </ul>
              </div>
              <div className="option">
                <span className="option-title">Option B – Future credit</span>
                <ul>
                  <li>100% credit for any future trip</li>
                  <li>Valid 24 months, transferable</li>
                  <li>Different itinerary or season</li>
                </ul>
              </div>
            </div>
            {/* <p className="small-note">If we cancel due to safety or force majeure: full credit or refund.</p> */}
          </section>

          {/* SECTION 3 */}
          <section className="grid-item">
            <h3>3. Changes to Booking / Itinerary</h3>
            <ul>
              <li>Date changes may be possible with admin fee.</li>
              <li>Itineraries may change due to weather, safety, or regulations.</li>
              <li>No refunds for minor adjustments or unused services.</li>
            </ul>
          </section>

          {/* SECTION 4 */}
          <section className="grid-item">
            <h3>4. Travel Documents & Permits</h3>
            <p>Traveler is responsible for valid passport, visa, permits. We assist — approval not guaranteed.</p>
          </section>

          {/* SECTION 5 */}
          <section className="grid-item">
            <h3>5. Health & Fitness</h3>
            <p>High‑altitude trekking requires good physical condition. Medical conditions must be disclosed in advance.</p>
          </section>

          {/* SECTION 6 – mandatory insurance */}
          <section className="grid-item">
            <h3>6. Mandatory Insurance</h3>
            <ul>
              <li>Emergency evacuation / helicopter rescue</li>
              <li>Repatriation</li>
              <li>Trip cancellation / interruption</li>
              <li>High‑altitude coverage</li>
            </ul>
            <p className="small-note">Min. coverage: USD 200,000–500,000</p>
          </section>

          {/* SECTION 7 – risk & liability (full width emphasis) */}
          <section className="grid-item full-width">
            <h3>7. Assumption of Risk & Liability</h3>
            <p>Adventure travel in Pakistan involves inherent risks: altitude sickness, extreme weather, remote terrain, and unpredictable conditions. Participation is voluntary and at your own risk. You release World Adventure Tours from any liability to the fullest extent permitted by law.</p>
          </section>

          {/* SECTION 8 */}
          <section className="grid-item">
            <h3>8. Force Majeure</h3>
            <p>Not liable for events beyond control: natural disasters, pandemics, government restrictions, etc.</p>
          </section>

          {/* SECTION 9 */}
          <section className="grid-item">
            <h3>9. Participant Responsibilities</h3>
            <ul>
              <li>Follow guide instructions</li>
              <li>Respect local laws & Islamic customs</li>
              <li>Protect environment & wildlife</li>
            </ul>
          </section>

          {/* SECTION 10 */}
          <section className="grid-item">
            <h3>10. Photographs & Marketing</h3>
            <p>We may use photos/videos for marketing. Inform us in writing if you prefer to be excluded.</p>
          </section>

          {/* SECTION 11 */}
          <section className="grid-item">
            <h3>11. Privacy</h3>
            <p>Personal data shared with authorities or suppliers for operational needs.</p>
          </section>

          {/* SECTION 12 */}
          <section className="grid-item">
            <h3>12. Governing Law</h3>
            <p>Governed by laws of Pakistan. Disputes resolved in Islamabad courts.</p>
          </section>
        </div>

        {/* PROFESSIONAL CONTACT DETAILS – redesigned, with physical address, phone, email, emergency */}
        <div className="contact-details">
          <h4>World Adventure Tours – Contact & Support</h4>
          <div className="contact-grid">
            <div className="contact-item">
              <span className="contact-icon">📍</span>
              <div>
                <strong>Head Office</strong>
                <p>Skardu Gilgit Baltistan, Pakistan</p>
              </div>
            </div>
            <div className="contact-item">
              <span className="contact-icon">📞</span>
              <div>
                <strong>Phone (24/7 emergency)</strong>
                <p>+971 55 593 6558</p>
                <span className="contact-sub">Office: +923 49 158 9717</span>
              </div>
            </div>
            <div className="contact-item">
              <span className="contact-icon">✉️</span>
              <div>
                <strong>Email</strong>
                <p>info@worldadventurestours.com</p>
                <span className="contact-sub">worldadventuretours@gmail.com</span>
              </div>
            </div>
            <div className="contact-item">
              <span className="contact-icon">🕒</span>
              <div>
                <strong>Office hours</strong>
                <p>Mon–Fri: 9:00 – 18:00 (PKT)</p>
                <span className="contact-sub">Sat: 10:00 – 14:00</span>
              </div>
            </div>
          </div>
        </div>

        {/* footer with strong confirmation and license */}
        <div className="terms-footer">
          <div className="footer-accent"></div>
          <p className="confirmation">
            <strong>By booking with World Adventure Tours, you agree to these Terms & Conditions.</strong>
          </p>
          <p className="license">
            World Adventure Tours is a licensed tour operator, member of the Association of Tour Operators of Pakistan.
          </p>
        </div>

      </div>
    </div>
  );
};

export default TermsAndConditions;